import React, { useState, useEffect } from "react";
import { Alert, Table, Form, Button, Spinner } from "react-bootstrap";
import axios from "axios";
import toast from "react-hot-toast";

/**
 * ABHA capture + display. Uses PATCH /patient/:id/abdm (raw Mongo _id).
 */
function AbdmPatientSection({ patientId, abdm, onSaved }) {
  const [abhaNumber, setAbhaNumber] = useState("");
  const [abhaAddress, setAbhaAddress] = useState("");
  const [linkStatus, setLinkStatus] = useState("none");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAbhaNumber(abdm?.abha_number ?? "");
    setAbhaAddress(abdm?.abha_address ?? "");
    setLinkStatus(abdm?.link_status ?? "none");
  }, [abdm, patientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientId) {
      toast.error("Patient id missing — reload the page.");
      return;
    }

    const body = {
      abha_number: abhaNumber.trim(),
      abha_address: abhaAddress.trim(),
      link_status: linkStatus,
    };

    if (linkStatus === "linked" && !abdm?.linked_at) {
      body.linked_at = new Date().toISOString();
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.patch(
        `${process.env.REACT_APP_API_URL}/patient/${patientId}/abdm`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("ABHA / ABDM details saved.");
      onSaved?.(data.patient);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Save failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const summaryRows = abdm
    ? [
        ["Link status", abdm.link_status],
        ["ABHA number", abdm.abha_number],
        ["ABHA address", abdm.abha_address],
        [
          "Linked at",
          abdm.linked_at ? new Date(abdm.linked_at).toLocaleString() : null,
        ],
      ].filter(([, v]) => v != null && v !== "")
    : [];

  return (
    <div>
      {!patientId ? (
        <Alert variant="warning" className="mb-3">
          Open a saved patient record to capture ABHA (patient id not loaded).
        </Alert>
      ) : null}

      {summaryRows.length > 0 ? (
        <Table bordered={false} className="tableItems mb-3">
          <thead>
            <tr>
              <th colSpan={2}>Current linkage</th>
            </tr>
          </thead>
          <tbody>
            {summaryRows.map(([label, val]) => (
              <tr key={label}>
                <td className="itemHeading">{label}</td>
                <td>{String(val)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Alert variant="secondary" className="mb-3">
          No ABHA stored yet. Enter details below and save.
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-2" controlId="abhaNumber">
          <Form.Label>ABHA number</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. 14-digit ABHA or as issued"
            value={abhaNumber}
            onChange={(e) => setAbhaNumber(e.target.value)}
            autoComplete="off"
          />
        </Form.Group>
        <Form.Group className="mb-2" controlId="abhaAddress">
          <Form.Label>ABHA address (optional)</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. user@sbx or PHR handle"
            value={abhaAddress}
            onChange={(e) => setAbhaAddress(e.target.value)}
            autoComplete="off"
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="linkStatus">
          <Form.Label>Link status</Form.Label>
          <Form.Select
            value={linkStatus}
            onChange={(e) => setLinkStatus(e.target.value)}
          >
            <option value="none">None</option>
            <option value="pending">Pending</option>
            <option value="linked">Linked</option>
            <option value="error">Error</option>
          </Form.Select>
        </Form.Group>
        <Button type="submit" variant="primary" disabled={saving || !patientId}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            "Save ABHA / ABDM"
          )}
        </Button>
      </Form>
    </div>
  );
}

export default AbdmPatientSection;
